import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ASSET_TYPES, MATURITY_LEVELS, CERTAINTY_LEVELS } from "./constants";
import { Plus, Archive, CaretDown, CaretUp, MagnifyingGlass } from "@phosphor-icons/react";
import { ArchiveDialog } from "./ArchiveManager";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SCOPES = { global: "Global", regional_apac: "Regional (APAC)", regional_europe: "Regional (Europe)", local: "Local / single market" };

const EMPTY_FORM = {
  name: "", type: "pivotal_rct", scope: "global", localized_for: [], description: "", maturity: "published",
  evidence_certainty: "auto", authors: "", journal: "", publication_year: "", doi_or_registry_id: "", source_url: "", internal_analysis: false,
};
const CITATION_INPUTS = [
  ["authors", "Authors", "e.g. Sharma S, Baumbach A, et al."],
  ["journal", "Journal / Registry name", "e.g. The Lancet / ClinicalTrials.gov"],
  ["publication_year", "Publication year", "e.g. 2025"],
  ["doi_or_registry_id", "DOI or Trial Registry ID", "e.g. 10.1016/… or NCT05135910"],
  ["source_url", "Source URL", "https://…"],
];

export const AssetManager = ({ productId, assets, countries, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const toggleMarket = (id) =>
    setForm((f) => ({ ...f, localized_for: f.localized_for.includes(id) ? f.localized_for.filter((x) => x !== id) : [...f.localized_for, id] }));

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Asset name is required");
    if (!form.internal_analysis) {
      const missing = CITATION_INPUTS.filter(([k]) => !String(form[k]).trim()).map(([, l]) => l);
      if (missing.length > 0) return toast.error(`Citation required: ${missing.join(", ")} — or tag as Internal Analysis`);
    }
    setSaving(true);
    try {
      await axios.post(`${API}/evidence/${productId}`, {
        ...form,
        evidence_certainty: form.evidence_certainty === "auto" ? "" : form.evidence_certainty,
        publication_year: form.publication_year ? Number(form.publication_year) : null,
      });
      toast.success("Evidence asset added — reusability recomputed");
      setOpen(false);
      setForm(EMPTY_FORM);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add asset");
    } finally {
      setSaving(false);
    }
  };

  const [confirmId, setConfirmId] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState("");
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const q = query.trim().toLowerCase();
  const filtered = q ? assets.filter((a) => a.name.toLowerCase().includes(q)) : assets;
  const showList = expanded || q.length > 0;

  return (
    <section className="bg-white border border-slate-200">
      <div className="p-6 border-b border-slate-200 flex items-center justify-between">
        <div>
          <p className="overline-label text-slate-400">Evidence Portfolio</p>
          <h2 className="font-heading font-bold text-lg tracking-tight mt-1">Evidence Assets ({assets.length})</h2>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-asset-btn" className="bg-slate-900 text-white hover:bg-slate-700 rounded-none gap-2">
              <Plus size={16} weight="bold" /> Add Evidence Asset
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="add-asset-dialog" className="bg-white sm:max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold tracking-tight">New Evidence Asset</DialogTitle>
              <DialogDescription className="text-sm text-slate-500">Add an evidence asset — acceptance across all markets is recomputed instantly.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <p className="overline-label text-slate-400 mb-1.5">Name</p>
                <Input data-testid="asset-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Korea single-arm durability study" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="overline-label text-slate-400 mb-1.5">Type</p>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="asset-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {Object.entries(ASSET_TYPES).map(([k, v]) => (
                        <SelectItem key={k} value={k} data-testid={`asset-type-${k}`}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="overline-label text-slate-400 mb-1.5">Scope</p>
                  <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
                    <SelectTrigger data-testid="asset-scope-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {Object.entries(SCOPES).map(([k, v]) => (
                        <SelectItem key={k} value={k} data-testid={`asset-scope-${k}`}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <p className="overline-label text-slate-400 mb-1.5">Evidence maturity</p>
                <Select value={form.maturity} onValueChange={(v) => setForm({ ...form, maturity: v })}>
                  <SelectTrigger data-testid="asset-maturity-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {Object.entries(MATURITY_LEVELS).map(([k, v]) => (
                      <SelectItem key={k} value={k} data-testid={`asset-maturity-${k}`}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <p className="overline-label text-slate-400 mb-1.5">Evidence certainty (GRADE-style)</p>
                <Select value={form.evidence_certainty} onValueChange={(v) => setForm({ ...form, evidence_certainty: v })}>
                  <SelectTrigger data-testid="asset-certainty-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="auto" data-testid="asset-certainty-auto">Auto (default by study type)</SelectItem>
                    {Object.entries(CERTAINTY_LEVELS).map(([k, v]) => (
                      <SelectItem key={k} value={k} data-testid={`asset-certainty-${k}`}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="border border-slate-200 p-3 space-y-3">
                <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                  <Checkbox data-testid="asset-internal-analysis" checked={form.internal_analysis}
                    onCheckedChange={(v) => setForm({ ...form, internal_analysis: !!v })} />
                  Internal Analysis — Not Independently Verifiable
                </label>
                {!form.internal_analysis && (
                  <div className="space-y-2.5">
                    <p className="text-[10px] text-slate-400">Citation is mandatory for external evidence — all fields below are required.</p>
                    {CITATION_INPUTS.map(([k, label, ph]) => (
                      <div key={k}>
                        <p className="overline-label text-slate-400 mb-1">{label}</p>
                        <Input data-testid={`asset-citation-${k}`} value={form[k]}
                          type={k === "publication_year" ? "number" : "text"}
                          onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={ph} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <p className="overline-label text-slate-400 mb-1.5">Localized for markets</p>
                <div className="grid grid-cols-2 gap-2 border border-slate-200 p-3">
                  {countries.map((c) => (
                    <label key={c.id} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                      <Checkbox data-testid={`asset-market-${c.id}`} checked={form.localized_for.includes(c.id)} onCheckedChange={() => toggleMarket(c.id)} />
                      {c.flag} {c.name}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <p className="overline-label text-slate-400 mb-1.5">Description</p>
                <Textarea data-testid="asset-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
              </div>
              <Button data-testid="asset-submit-btn" onClick={submit} disabled={saving} className="w-full bg-slate-900 text-white hover:bg-slate-700 rounded-none">
                {saving ? "Adding…" : "Add Asset & Recompute"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="px-6 py-3 border-b border-slate-200 flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="asset-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search assets…" className="pl-8 h-8 text-sm rounded-none" />
        </div>
        <button data-testid="toggle-asset-list-btn" onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900">
          {showList ? <>Collapse list <CaretUp size={12} weight="bold" /></> : <>Show all {assets.length} assets <CaretDown size={12} weight="bold" /></>}
        </button>
      </div>
      {showList && (
        <div className="divide-y divide-slate-100">
          {filtered.length === 0 && <p className="px-6 py-4 text-sm text-slate-400">No assets match “{query}”.</p>}
          {filtered.map((a) => (
            <div key={a.id} data-testid={`asset-row-${a.id}`} className="px-6 py-3.5 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900">{a.name}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {ASSET_TYPES[a.type]} · {SCOPES[a.scope]}{a.maturity ? ` · ${MATURITY_LEVELS[a.maturity] || a.maturity}` : ""}{a.localized_for.length > 0 && ` · localized: ${a.localized_for.join(", ")}`}
                </p>
              </div>
              {isAdmin && (
                <button data-testid={`archive-asset-${a.id}`} onClick={() => setConfirmId(a.id)}
                  className="text-slate-300 hover:text-red-600 p-2" aria-label="Archive asset" title="Archive (30-day recovery)">
                  <Archive size={16} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {confirmId && (
        <ArchiveDialog productId={productId} asset={assets.find((a) => a.id === confirmId)}
          onClose={() => setConfirmId(null)}
          onArchived={() => { setConfirmId(null); onChanged(); }} />
      )}
    </section>
  );
};
